import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AppointmentForm from "@/components/appointments/AppointmentForm";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { PlusCircle, Search, Calendar, Archive, Edit, Eye } from "lucide-react";
import { getAppointments, getAppointmentById, archiveAppointment } from "@/services/AppointmentService";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AppointmentDetailView } from "@/components/appointments/AppointmentDetailView";

export default function Appointments() {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const clientId = searchParams.get("clientId");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(!!clientId);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [appointmentToArchive, setAppointmentToArchive] = useState<string | null>(null);

  const { 
    data: appointmentsResult, 
    isLoading, 
    refetch 
  } = useQuery({
    queryKey: ['appointments', showArchived, clientId || 'all'],
    queryFn: () => getAppointments(showArchived, undefined, clientId || undefined),
  });

  const { data: selectedAppointmentResult } = useQuery({
    queryKey: ['appointment', selectedAppointmentId],
    queryFn: () => selectedAppointmentId ? getAppointmentById(selectedAppointmentId) : null,
    enabled: !!selectedAppointmentId,
  });

  const appointments = appointmentsResult?.data || [];
  const selectedAppointment = selectedAppointmentResult?.data;

  const filteredAppointments = appointments.filter(appointment => 
    appointment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (appointment.client?.name && appointment.client.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    appointment.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditAppointment = (appointmentId: string) => {
    setSelectedAppointmentId(appointmentId);
    setIsEditDialogOpen(true);
  };

  const handleViewAppointment = (appointmentId: string) => {
    setSelectedAppointmentId(appointmentId);
    setIsDetailDialogOpen(true);
  };

  const handleArchiveDialogOpen = (appointmentId: string) => {
    setAppointmentToArchive(appointmentId);
    setArchiveDialogOpen(true);
  };

  const handleArchiveAppointment = async () => {
    if (appointmentToArchive) {
      await archiveAppointment(appointmentToArchive);
      refetch();
      setArchiveDialogOpen(false);
      setAppointmentToArchive(null);
    }
  };

  const handleDialogClose = () => {
    setIsCreateDialogOpen(false);
    setIsEditDialogOpen(false);
    setIsDetailDialogOpen(false);
    setSelectedAppointmentId(null);
    refetch();
  };

  return (
    <DashboardLayout title="Compromissos" isLoading={isLoading}>
      <div className="space-y-6">
        <div className="flex items-center justify-end w-full mb-4">
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]">
                <PlusCircle className="mr-2 h-4 w-4" />
                Novo Compromisso
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] dark:bg-white">
              <DialogHeader>
                <DialogTitle>Agendar Compromisso</DialogTitle>
                <DialogDescription>
                  Preencha as informações para agendar um novo compromisso.
                </DialogDescription>
              </DialogHeader>
              <AppointmentForm 
                clientId={clientId || undefined} 
                onSuccess={handleDialogClose} 
              />
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 w-full">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Buscar por título, cliente ou tipo"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="show-archived"
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
            <Label htmlFor="show-archived" className="text-sm text-gray-700">
              Mostrar arquivados
            </Label>
          </div>
        </div>

        <div className="rounded-md border dark:bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Local</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-[200px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[150px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                  </TableRow>
                ))
              ) : filteredAppointments && filteredAppointments.length > 0 ? (
                filteredAppointments.map(appointment => {
                  const appointmentDate = new Date(appointment.date);
                  const hasTime = appointmentDate.getHours() !== 0 || appointmentDate.getMinutes() !== 0;
                  const dateFormat = hasTime ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy";
                  
                  return (
                    <TableRow key={appointment.id} className={appointment.archived ? "opacity-60 bg-gray-50 dark:bg-gray-800/50  dark:text-white" : " dark:text-gray-600"}>
                      <TableCell className="font-medium">
                        {appointment.title}
                        {appointment.archived && (
                          <span className="ml-2 text-xs text-gray-500">(Arquivado)</span>
                        )}
                      </TableCell>
                      <TableCell>{appointment.client?.name || "—"}</TableCell>
                      <TableCell>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          appointment.type === 'Audiência' 
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                            : appointment.type === 'Prazo'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                              : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                        }`}>
                          {appointment.type}
                        </span>
                      </TableCell>
                      <TableCell>
                        {format(appointmentDate, dateFormat, { locale: ptBR })}
                      </TableCell>
                      <TableCell>{appointment.location || "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleViewAppointment(appointment.id)}
                          >
                            <Eye className="h-4 w-4" />
                            <span className="sr-only">Ver detalhes</span>
                          </Button>
                          {!appointment.archived && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditAppointment(appointment.id)}
                              >
                                <Edit className="h-4 w-4" />
                                <span className="sr-only">Editar</span>
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleArchiveDialogOpen(appointment.id)}
                              >
                                <Archive className="h-4 w-4" />
                                <span className="sr-only">Arquivar</span>
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    {searchTerm ? 'Nenhum compromisso encontrado para a busca.' : 'Nenhum compromisso cadastrado.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Editar Compromisso</DialogTitle>
              <DialogDescription>
                Faça as alterações necessárias no compromisso.
              </DialogDescription>
            </DialogHeader>
            {selectedAppointment && (
              <AppointmentForm 
                appointment={selectedAppointment}
                onSuccess={handleDialogClose} 
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Detalhes do Compromisso</DialogTitle>
              <DialogDescription>
                Visualize todas as informações do compromisso selecionado.
              </DialogDescription>
            </DialogHeader>
            {selectedAppointment && (
              <AppointmentDetailView 
                appointment={selectedAppointment}
                onEdit={() => {
                  setIsDetailDialogOpen(false);
                  setIsEditDialogOpen(true);
                }}
                onClose={() => setIsDetailDialogOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Archive Dialog */}
        <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Arquivar compromisso</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja arquivar este compromisso? Compromissos arquivados não são exibidos por padrão nas listagens.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleArchiveAppointment} className="flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]">
                Arquivar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}